import MaterialTable, { MTableCell, MTableToolbar } from '@material-table/core'
import { Button, Chip, Icon, IconButton, MenuItem, Typography, useTheme } from '@material-ui/core'
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles'
import {
	AddOutlined,
	ArrowBackIosOutlined,
	ArrowForwardIosOutlined,
	CalendarToday,
	Warning
} from '@material-ui/icons'
import { KeyboardDatePicker } from '@material-ui/pickers'
import clsx from 'clsx'
import moment from 'moment'
import React, { Fragment, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { guestSigned, removeCheckIn } from '../../actions/checkIns/checkins.actions'
import { getChipStyles } from '../../services/helpers'
import { table } from '../../services/theme'
import {
	checkIfNeedsRenewalAndShouldBeNotified,
	checkIfPetIsBanned,
	checkIfPetHasCompletedGrooming,
	getIconAndTagByFoundingAndMembership
} from '../../utils'
import AddCheckInDialog from '../Dialogs/AddCheckInDialog'
import BadCheckInDialog from '../Dialogs/BadCheckInDialog'
import CheckInDialog from '../Dialogs/CheckInDialog'
import TableDropdown from './TableDropdown'
import { purple, gray, dangers, successes } from '../../utils/colors'

const useStyles = makeStyles((theme) => ({
	headerButton: {
		fontFamily: 'Nunito',
		fontWeight: '700',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	addClientButton: {
		color: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		padding: '7px 40px 8px 24px',
		'& .MuiButton-label': {
			'& .MuiButton-startIcon': {
				marginRight: 16
			}
		}
	},
	toolbarWrapper: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: gray[200]
	},
	petStatusIcon: {
		position: 'absolute',
		left: 0
	},
	toolbarExtras: {
		display: 'flex',
		'& > *': {
			marginRight: '16px'
		}
	},
	toolbar: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		padding: '24px 25px',
		'& > div:nth-child(1)': {
			'& > div:nth-child(4)': {
				flex: 'auto'
			}
		}
	},
	datePickerContainer: {
		display: 'flex',
		alignItems: 'center',
		position: 'relative'
	},
	datePicker: {
		color: gray[100],
		opacity: 0,
		position: 'absolute',
		top: 0,
		height: 38,
		margin: 0,
		'& *': {
			height: 0,
			padding: 0,
			margin: 0
		}
	},
	calendarButton: {
		'& svg': {
			fill: theme.palette.primary.main
		}
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	},
	calendarDateButton: {
		zIndex: 1,
		border: `1px solid ${gray[500]}`,
		padding: '6px 16px 6px 24px',
		color: gray[800],
		fontWeight: '700',
		'& .MuiButton-label': {
			'& .MuiButton-endIcon': {
				marginLeft: 16
			},
			whiteSpace: 'nowrap'
		}
	},
	dateChangeButton: {
		border: `1px solid ${gray[500]}`,
		height: 38,
		width: 38,
		padding: 5,
		'& svg': {
			height: 16
		}
	},
	addCheckInButton: {
		color: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		padding: '7px 40px 8px 24px',
		'& .MuiButton-label': {
			'& .MuiButton-startIcon': {
				marginRight: 16
			}
		}
	},
	scheduleAggregatesWrapper: {
		display: 'flex',
		flexWrap: 'wrap',
		paddingTop: '10px',
		paddingBottom: '12px'
	},
	scheduleAggregate: {
		padding: '4px 16px',
		border: `1px solid ${gray[500]}`,
		borderRadius: 6,
		color: gray[800],
		cursor: 'pointer',
		marginRight: 16,
		marginTop: 5,
		marginBottom: 5,
		'&:hover': {
			color: theme.palette.secondary.main,
			borderColor: theme.palette.secondary.main
		},
		'&.active': {
			color: '#FFFFFF',
			backgroundColor: theme.palette.secondary.main
		},
		'&:nth-child(1)': {
			marginLeft: 0
		}
	},
	spacer: {
		margin: '5px 16px 5px 0px',
		width: '24px',
		position: 'relative',
		'&:before': {
			content: "''",
			position: 'absolute',
			height: '1px',
			width: '100%',
			top: 'calc(50% - 1px)',
			left: 0,
			backgroundColor: gray[300]
		}
	},
	chip: {
		'& > span': { marginRight: '0px' }
	},
	membershipWrapper: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center'
	},
	membershipIcon: {
		color: purple[500],
		'&::before': {
			fontSize: '14px',
			lineHeight: '14px'
		}
	},
	membershipType: {
		fontFamily: 'Nunito',
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '19px',
		color: gray[700],
		textTransform: 'capitalize',
		marginTop: 4
	}
}))

const NOT_SHOWN_AGGREGATES = ['arrived', 'departed']

export const BarAndParkTable = ({
	addNewVisitor,
	changeDate,
	checkIns,
	completeCheckIn,
	date,
	dogsCheckedIn,
	facility,
	membershipTypes,
	peopleCheckedIn,
	petsInFacility,
	scheduleAggregates,
	setDate,
	setAggregateActive,
	singleLoading,
	refreshList,
	removeAccount,
	removeGuest,
	removePet
}) => {
	const theme = useTheme()
	const classes = useStyles()
	const dispatch = useDispatch()
	const checkInsLoading = useSelector((state) => state.specific.checkIns.loading)
	const parking_codes = useSelector((state) => state.specific.parking.parking_codes)
	const locked_codes = useSelector((state) => state.specific.parking.locked)

	const [checkInShown, setCheckInShown] = useState(false)
	const [badCheckIn, setBadCheckIn] = useState(false)
	const [selectedCheckIn, setSelectedCheckIn] = useState({})
	const [isAddCheckinDialogShown, setIsAddCheckinDialogShown] = useState(false)
	const [parkingCode, setCodeOnCheckIn] = useState(null)
	const [isOpen, setIsOpen] = useState(false)

	const renderElegibility = (data) => {
		if (data.hasIssues) {
			return (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{data.pets.some(
						(p) =>
							p.issues &&
							p.issues.length &&
							p.issues.some((i) => i.type === 'vaccine' || i.type === 'red')
					) ? (
						<Chip
							label={<Warning style={{ fontSize: '15px' }} />}
							style={{
								...getChipStyles('rejected'),
								...{ paddingTop: '6px', marginRight: 8 }
							}}
						/>
					) : null}
					<Chip
						label={<Warning style={{ fontSize: '15px' }} />}
						style={{ ...getChipStyles('warning'), ...{ paddingTop: '6px' } }}
					/>
				</div>
			)
		}

		return (
			<Chip
				label={<Icon className="fas fa-check" style={{ fontSize: '15px', fill: '#2E7B32' }} />}
				className={classes.chip}
				style={{
					...getChipStyles('success'),
					paddingTop: '6px',
					marginTop: 8
				}}
			/>
		)
	}

	const renderClientName = (data) => {
		const [iconName, iconColor, tag] = getIconAndTagByFoundingAndMembership({
			...data.household,
			memberships: data.memberships || []
		})

		return (
			<div>
				{data.accounts && data.accounts.length > 0 ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center'
						}}>
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<Typography
								style={{
									color: gray[900],
									fontWeight: '600',
									fontSize: '16px',
									padding: '4px'
								}}>
								{data.accounts.map((account) => account.full_name).join(', ')}
							</Typography>
						</div>
						{data.guests && data.guests.length > 0 ? (
							<Chip
								label={'+' + data.guests.length}
								style={{
									...getChipStyles('checkin-guests'),
									paddingTop: '4px',
									marginLeft: 6
								}}
							/>
						) : null}
					</div>
				) : null}
				{!!iconName || !!tag ? (
					<div className={classes.membershipWrapper}>
						{!!iconName && (
							<Icon
								className={`fa fa-${iconName}`}
								style={{
									color: iconColor,
									fontWeight: '900',
									fontSize: '14px',
									lineHeight: '16px',
									width: 20
								}}
							/>
						)}
						<Typography className={classes.membershipType}>{tag}</Typography>
					</div>
				) : null}
			</div>
		)
	}

	const renderPets = (data, classes) => (
		<div>
			{data.pets && data.pets.length > 0 ? (
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<Typography
						style={{
							color: gray[900],
							padding: '4px',
							fontWeight: '500',
							fontSize: '16px'
						}}>
						{data.pets.map((pet) => pet.name).join(', ')}
					</Typography>
				</div>
			) : null}
		</div>
	)

	const getSelected = () => {
		if (!selectedCheckIn || !checkIns) return null
		let ours = checkIns.find((c) => c._id === selectedCheckIn)
		return ours
	}

	const updateDate = (date) => {
		setDate(moment(date).format('ll'))
		setIsOpen(false)
	}

	const renderEngagement = (data) => {
		let bannedFromBarAndPark = false
		let bannedFromPetCare = false
		let hasCompletedGrooming = false
		let bannedFromGrooming = false
		const [needsRenewal, shouldBeNotified] = checkIfNeedsRenewalAndShouldBeNotified(
			data.household,
			data.memberships
		)

		if (data.household) {
			hasCompletedGrooming = data.household.completed_grooming_visit || false
		}

		if (data.pets && data.pets.length) {
			bannedFromBarAndPark = data && data.pets && data.pets.some((p) => checkIfPetIsBanned(p, 'Bar & Park'))
			bannedFromPetCare =
				data && data.pets && data.pets.some((p) => checkIfPetIsBanned(p, 'Daycare & Boarding'))
			bannedFromGrooming = data && data.pets && data.pets.some((p) => checkIfPetIsBanned(p, 'Grooming'))
		}

		const getIconColor = (banned) => (banned ? dangers[500] : successes[500])
		const getTextColor = (banned) => (banned ? dangers[500] : successes[500])

		return (
			<Fragment>
				{(data.household && !data.household.hasUsedBarAndPark) || bannedFromBarAndPark ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-${bannedFromBarAndPark ? 'times' : 'seedling'}`}
							style={{
								color: getIconColor(bannedFromBarAndPark),
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: getTextColor(bannedFromBarAndPark),
								width: '100%',
								display: 'block'
							}}>
							Bar & Park
						</Typography>
					</div>
				) : null}
				{(data.household && !data.household.hasUsedPetCare) || bannedFromPetCare ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-${bannedFromPetCare ? 'times' : 'seedling'}`}
							style={{
								color: getIconColor(bannedFromPetCare),
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: getTextColor(bannedFromPetCare),
								width: '100%',
								display: 'block'
							}}>
							Pet Care
						</Typography>
					</div>
				) : null}
				{!hasCompletedGrooming ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-seedling`}
							style={{
								color: successes[500],
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: successes[500],
								width: '100%',
								display: 'block'
							}}>
							Grooming
						</Typography>
					</div>
				) : null}
				{bannedFromGrooming ? (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-times`}
							style={{
								color: getIconColor(bannedFromGrooming),
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: getTextColor(bannedFromGrooming),
								width: '100%',
								display: 'block'
							}}>
							Grooming
						</Typography>
					</div>
				) : null}
				{needsRenewal && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center'
						}}>
						<Icon
							className={`fas fa-${shouldBeNotified ? 'bell' : 'bell-slash'}`}
							style={{
								color: shouldBeNotified ? successes[500] : gray[600],
								fontSize: '12px',
								lineHeight: '12px',
								marginRight: '12px'
							}}
						/>
						<Typography
							variant="caption"
							style={{
								color: shouldBeNotified ? successes[500] : gray[600],
								width: '100%',
								display: 'block'
							}}>
							Needs Renewal
						</Typography>
					</div>
				)}
			</Fragment>
		)
	}

	return (
		<div style={{ width: '100%', height: '100%' }}>
			<MuiThemeProvider theme={table}>
				<MaterialTable
					scheduleAggregates={scheduleAggregates}
					options={{
						headerStyle: {
							color: gray[900],
							textTransform: 'uppercase',
							fontWeight: 800,
							fontSize: '12px',
							lineHeight: '16px',
							padding: '11px 24px',
							borderTop: `1px solid ${gray[500]}`,
							backgroundColor: gray[200]
						},
						rowStyle: {
							verticalAlign: 'top'
						},
						pageSize: 50,
						pageSizeOptions: [10, 20, 50],
						toolbarButtonAlignment: 'left',
						searchFieldAlignment: 'left',
						searchFieldVariant: 'filled',
						actionsCellStyle: {
							paddingTop: 8,
							border: 'none'
						},
						actionsColumnIndex: -1
					}}
					isLoading={checkInsLoading || singleLoading}
					columns={[
						{
							title: 'Client',
							render: (data) => renderClientName(data),
							customFilterAndSearch: (value, rowData) => {
								let { accounts } = rowData
								let name = value
								if (name) name = name.toUpperCase()
								return accounts && accounts.length > 0
									? accounts.findIndex(
											(account) =>
												(account.first_name &&
													account.first_name.toUpperCase().includes(name)) ||
												(account.last_name &&
													account.last_name.toUpperCase().includes(name))
									  ) !== -1
									: false
							}
						},
						{
							title: 'Phone',
							field: 'type',
							render: (data) => {
								if (data.accounts[0] && data.accounts[0].phone_number) {
									//clean phone number remove all non digits
									let phoneNumber = data.accounts[0].phone_number.replace(/\D/g, '')

									//validates it's 10 digits long
									if (/^\d{10}$/.test(phoneNumber))
										return (
											phoneNumber.substr(0, 3) +
											'-' +
											phoneNumber.substr(3, 3) +
											'-' +
											phoneNumber.substr(6, 4)
										)
								}

								return 'N/A'
							}
						},
						{
							title: 'Dog(s)',
							field: 'pets',
							render: (data) => renderPets(data),
							customFilterAndSearch: (value, rowData) => {
								let { pets } = rowData
								let name = value
								if (name) name = name.toUpperCase()
								return pets && pets.length > 0
									? pets.findIndex((pet) => pet.name.toUpperCase().includes(name)) !== -1
									: false
							}
						},
						{
							title: 'Eligibility',
							render: (data) => renderElegibility(data)
						},
						{
							title: 'Status',
							field: 'status.name',
							render: (data) => {
								const checkedIn =
									data.checked_in &&
									data.checked_in_accounts &&
									data.checked_in_guests &&
									data.account_ids &&
									data.guests &&
									data.checked_in_accounts.length === data.account_ids.length
								// data.checked_in_guests.length === data.guests.length

								return (
									<div>
										<Chip
											label={
												data.cancelled
													? 'Cancelled'
													: data.expired && !data.time_checked_in
													? 'Expired'
													: data.expired && data.time_checked_in
													? 'Completed'
													: checkedIn
													? 'Checked In'
													: 'Waiting'
											}
											style={getChipStyles(
												(data.expired || data.cancelled) && !data.time_checked_in
													? 'cancelled'
													: checkedIn || data.time_checked_in
													? 'checked in'
													: null
											)}
										/>
										{checkedIn && data.time_checked_in && <Typography variant="body1" style={{ fontSize: 14, marginTop: 4 }}>{moment(data.time_checked_in, 'MMMM Do, YYYY, h:mm a').tz(facility.time_zone || 'America/New_York').format('h:mm a')}</Typography>}
									</div>
								)
							}
						},
						{
							title: 'Engagement',
							render: (data) => renderEngagement(data)
						}
					]}
					data={checkIns || []}
					onRowClick={(e, checkin) => {
						setSelectedCheckIn(checkin._id)
						setCheckInShown(true)
					}}
					actions={[{}]}
					components={{
						Action: (props) => (
							<TableDropdown>
								<MenuItem onClick={() => dispatch(removeCheckIn(props.data._id))}>
									Delete Record
								</MenuItem>
							</TableDropdown>
						),
						Cell: (props) => (
							<MTableCell
								style={{
									borderBottom: 'none',
									padding: '16px 36px 16px 24px'
								}}
								{...props}
							/>
						),
						Toolbar: (props) => (
							<div className={classes.toolbarWrapper}>
								<div className={classes.toolbar}>
									<MTableToolbar {...props} />
									<div className={classes.toolbarExtras}>
										<div className={classes.datePickerContainer}>
											<Button
												className={clsx(classes.headerButton, classes.calendarDateButton)}
												onClick={() => setIsOpen(true)}
												variant="outlined"
												endIcon={
													<CalendarToday
														style={{ height: 19, width: 19, fill: gray[700] }}
													/>
												}
												size="large">
												{date}
											</Button>
											<KeyboardDatePicker
												disableToolbar
												open={isOpen}
												onOpen={() => setIsOpen(true)}
												onClose={() => setIsOpen(false)}
												variant="inline"
												format="MM/DD/YYYY"
												margin="normal"
												id="start-date"
												label="Start Date"
												inputVariant="filled"
												value={date}
												onChange={updateDate}
												className={classes.datePicker}
												rightArrowIcon={
													<ArrowForwardIosOutlined
														className={classes.calendarHeaderButtons}
													/>
												}
												leftArrowIcon={
													<ArrowBackIosOutlined
														className={classes.calendarHeaderButtons}
													/>
												}
												KeyboardButtonProps={{
													'aria-label': 'Start Date',
													className: classes.calendarButton
												}}
											/>
										</div>
										<IconButton
											className={classes.dateChangeButton}
											onClick={() => changeDate('subtract')}>
											<ArrowBackIosOutlined style={{ fill: gray[700] }} />
										</IconButton>
										<IconButton
											className={classes.dateChangeButton}
											onClick={() => changeDate('add')}>
											<ArrowForwardIosOutlined style={{ fill: gray[700] }} />
										</IconButton>
										<Button
											variant="outlined"
											className={clsx(classes.headerButton, classes.addCheckInButton)}
											startIcon={
												<AddOutlined style={{ fill: theme.palette.secondary.main }} />
											}
											size="large"
											onClick={() => setIsAddCheckinDialogShown(true)}>
											Check In
										</Button>
										{/* Export button not currently functioning TODO */}
										{/* <Button
                    variant="outlined"
                    className={clsx(
                      classes.headerButton,
                      classes.addAppointmentButton
                    )}
                    startIcon={
                      <Print style={{ fill: theme.palette.secondary.main }} />
                    }
                    size="large"
                    onClick={() =>
                      history.push(
                        `/facilities/${params.facilityId}/appointment/create`
                      )
                    }
                  >
                    Export
                  </Button> */}
									</div>
								</div>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										padding: '16px 25px',
										borderTop: `1px solid ${gray[500]}`
									}}>
									<div>
										<Typography variant="overline">Filter By</Typography>
									</div>
									<div className={classes.scheduleAggregatesWrapper}>
										{scheduleAggregates
											? Object.keys(scheduleAggregates).map((aggregate) =>
													NOT_SHOWN_AGGREGATES.includes(
														aggregate
													) ? null : !aggregate.includes('spacer') ? (
														<div
															className={clsx(classes.scheduleAggregate, {
																active: scheduleAggregates[aggregate].active
															})}
															key={aggregate}
															onClick={() => setAggregateActive(aggregate)}>
															<Typography
																style={{
																	lineHeight: '16px',
																	fontWeight: 700,
																	fontSize: '12px',
																	textTransform: 'uppercase'
																}}>
																{aggregate}
															</Typography>
															<Typography
																style={{ fontWeight: 800, lineHeight: '22px' }}>
																{scheduleAggregates
																	? scheduleAggregates[aggregate].value
																	: 0}
															</Typography>
														</div>
													) : (
														<div key={aggregate} className={classes.spacer}></div>
													)
											  )
											: null}
										<div
											className={clsx(classes.scheduleAggregate)}
											style={{ marginLeft: 36, borderWidth: 0 }}>
											<Typography
												style={{
													lineHeight: '16px',
													fontWeight: 700,
													fontSize: '12px',
													textTransform: 'uppercase'
												}}>
												PEOPLE CHECKED IN
											</Typography>
											<Typography style={{ fontWeight: 800, lineHeight: '22px' }}>
												{peopleCheckedIn}
											</Typography>
										</div>
										<div
											className={clsx(classes.scheduleAggregate)}
											style={{ borderWidth: 0 }}>
											<Typography
												style={{
													lineHeight: '16px',
													fontWeight: 700,
													fontSize: '12px',
													textTransform: 'uppercase'
												}}>
												DOGS CHECKED IN
											</Typography>
											<Typography style={{ fontWeight: 800, lineHeight: '22px' }}>
												{dogsCheckedIn}
											</Typography>
										</div>
									</div>
								</div>
							</div>
						)
					}}
					localization={{
						header: {
							actions: ''
						}
					}}
					title="Bar & Park"
				/>
				<BadCheckInDialog
					isShown={badCheckIn}
					setIsShown={setBadCheckIn}
					checkIn={getSelected()}
					completeCheckIn={completeCheckIn}
					parkingCode={parkingCode}
					setCodeOnCheckIn={setCodeOnCheckIn}
				/>
			</MuiThemeProvider>
			<CheckInDialog
				parking_codes={parking_codes}
				locked_codes={locked_codes}
				parkingCode={parkingCode}
				setCodeOnCheckIn={setCodeOnCheckIn}
				addNewVisitor={addNewVisitor}
				unselect={() => setSelectedCheckIn(null)}
				date={date}
				isShown={checkInShown}
				setIsShown={setCheckInShown}
				completeBadCheckIn={() => setBadCheckIn(true)}
				checkIn={getSelected()}
				removePet={removePet}
				removeGuest={removeGuest}
				removeAccount={removeAccount}
				singleLoading={singleLoading}
				completeCheckIn={completeCheckIn}
				refreshList={refreshList}
				guestSigned={guestSigned}
				facility={facility}
				market_id={facility ? facility.market_id : ''}
				membershipTypes={membershipTypes}
				checkinId={selectedCheckIn}
				petsInFacility={petsInFacility}
			/>
			<AddCheckInDialog isShown={isAddCheckinDialogShown} setIsShown={setIsAddCheckinDialogShown} />
		</div>
	)
}
